import type { TreeData } from '@revelio/ui';
import { getPathToNode, isDirectMatchString } from '@revelio/ui';

// Constants
export const MIN_SEARCH_LENGTH = 2; // 10 seconds before showing "taking too long" message

// Helper Functions
export const insertNodeAtPath = (
  initialTree: TreeData[],
  nodes: TreeData[]
): TreeData[] => {
  return nodes.reduce((tree, node) => {
    const path = getPathToNode(node.id);
    return tree.map((item) => {
      if (path[0] === item.id) {
        if (!item.children) {
          item.children = [];
        }

        // Find the deepest existing parent
        let currentParent: TreeData = item;
        let foundExistingPath = true;

        // Skip the last element as that's the target node
        for (let i = 1; i < path.length - 1; i++) {
          const currentId = path[i];
          const child = currentParent.children?.find((c) => c.id === currentId);

          if (!child) {
            foundExistingPath = false;
            break;
          }

          currentParent = child;
        }

        if (foundExistingPath && currentParent.children) {
          // Check if the node already exists
          if (!currentParent.children.some((c) => c.id === node.id)) {
            // Find the correct index to insert the node based on nodeCount (descending)
            const nodeCount = node.nodeCount || 0; // Default to 0 if undefined
            const insertionIndex = currentParent.children.findIndex(
              (child) => (child.nodeCount || 0) < nodeCount
            );

            if (insertionIndex === -1) {
              // If no child has a smaller nodeCount, append to the end
              currentParent.children.push(node);
            } else {
              // Otherwise, insert at the found index
              currentParent.children.splice(insertionIndex, 0, node);
            }
          }
        }
      }
      return item;
    });
  }, initialTree);
};

// Helper to calculate the max nodeCount of children matching the search term
const getMaxMatchingChildNodeCount = (
  node: TreeData,
  searchTerm: string
): number => {
  const matchingChildrenCounts = node.children
    ?.filter((c) => isDirectMatchString(c.name, searchTerm))
    ?.map((c) => c.nodeCount || 0);
  // Use || [0] to handle cases where there are no children or no matching children, ensuring Math.max gets at least 0
  return Math.max(...(matchingChildrenCounts || [0]));
};

// Optimized non-mutating sort function
export const sortTreeDataByChildNodeCount = (
  data: TreeData[],
  searchTerm: string
): TreeData[] => {
  // 1. Create a new array with children sorted efficiently (non-mutating)
  const dataWithSortedChildren = data.map((item) => {
    // Create a sorted copy of children, calculating ranks only once per comparison
    const sortedChildren = item.children
      ?.slice() // Create a shallow copy
      .sort((a, b) => {
        const rankA = getMaxMatchingChildNodeCount(a, searchTerm);
        const rankB = getMaxMatchingChildNodeCount(b, searchTerm);
        return rankB - rankA; // Descending order
      });
    return {
      ...item,
      children: sortedChildren,
    };
  });

  // 2. Sort the new top-level array efficiently (non-mutating)
  const sortedData = dataWithSortedChildren.sort((a, b) => {
    // Get the highest-ranked child (already sorted in step 1)
    const firstChildA = a.children?.[0];
    const firstChildB = b.children?.[0];

    const rankA = firstChildA
      ? getMaxMatchingChildNodeCount(firstChildA, searchTerm)
      : 0;
    const rankB = firstChildB
      ? getMaxMatchingChildNodeCount(firstChildB, searchTerm)
      : 0;

    // Sort top-level items based on the rank of their highest-ranked child
    return rankB - rankA; // Descending order
  });

  return sortedData;
};
